import { createContext, useContext, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PATH_TRANSLATIONS, DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, WC_EN_TO_TR, WC_TR_TO_EN } from '@/i18n';

const LanguageContext = createContext();

export function useLanguage() {
  return useContext(LanguageContext);
}

// Reverse lookup: find lang + route key from a localized path segment
function findRouteFromPath(segment) {
  for (const [lang, paths] of Object.entries(PATH_TRANSLATIONS)) {
    for (const [key, val] of Object.entries(paths)) {
      if (val === segment) return { lang, key };
    }
  }
  return null;
}

// Detect browser language
function detectBrowserLanguage() {
  const browserLang = navigator.language?.split('-')[0] || 'en';
  const supported = SUPPORTED_LANGUAGES.find(l => l.code === browserLang);
  return supported ? supported.code : DEFAULT_LANGUAGE;
}

export function LanguageProvider({ children }) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Extract current lang from URL path
  const pathParts = location.pathname.split('/').filter(Boolean);
  const urlLang = SUPPORTED_LANGUAGES.find(l => l.code === pathParts[0])?.code;
  
  // Auto-detect: if root "/" with no lang prefix, redirect to detected language
  const isRootWithoutLang = location.pathname === '/' || (pathParts.length === 0);
  const currentLang = urlLang || (isRootWithoutLang ? detectBrowserLanguage() : DEFAULT_LANGUAGE);

  // Keep i18n in sync
  if (i18n.language !== currentLang) {
    i18n.changeLanguage(currentLang);
  }

  const getLocalizedPath = useCallback((routeKey, slug, lang) => {
    const targetLang = lang || currentLang;
    const paths = PATH_TRANSLATIONS[targetLang] || PATH_TRANSLATIONS.en;
    const segment = paths[routeKey] || routeKey;
    if (slug) return `/${targetLang}/${segment}/${slug}`;
    return `/${targetLang}/${segment}`;
  }, [currentLang]);

  const switchLanguage = useCallback((newLang) => {
    if (newLang === currentLang) return;
    i18n.changeLanguage(newLang);

    const parts = location.pathname.split('/').filter(Boolean);
    if (parts.length === 0) {
      navigate(`/${newLang}`);
      return;
    }

    const routeParts = urlLang ? parts.slice(1) : parts;
    if (routeParts.length === 0) {
      navigate(`/${newLang}`);
      return;
    }

    // Check if this is a World Cup path (dunya-kupasi-2026 or world-cup-2026)
    const isWCPath = routeParts[0] === 'dunya-kupasi-2026' || routeParts[0] === 'world-cup-2026';

    if (isWCPath) {
      // Translate each WC segment based on target language
      const isTR = newLang === 'tr';
      const map = isTR ? WC_EN_TO_TR : WC_TR_TO_EN;
      const translatedParts = routeParts.map(seg => map[seg] || seg);
      navigate(`/${newLang}/${translatedParts.join('/')}${location.search}`);
      return;
    }

    // Standard path translation (non-WC)
    const firstSegment = routeParts[0];
    const routeInfo = findRouteFromPath(firstSegment);

    if (routeInfo) {
      const newPaths = PATH_TRANSLATIONS[newLang] || PATH_TRANSLATIONS.en;
      const newSegment = newPaths[routeInfo.key] || firstSegment;
      const rest = routeParts.slice(1).join('/');
      navigate(`/${newLang}/${newSegment}${rest ? '/' + rest : ''}${location.search}`);
    } else {
      // Check if first segment is a sport name that needs translation
      const sportMap = { futbol: 'football', fussball: 'football', football: 'football', basketbol: 'basketball', basketball: 'basketball' };
      const sportKey = sportMap[firstSegment];
      if (sportKey) {
        const newPaths = PATH_TRANSLATIONS[newLang] || PATH_TRANSLATIONS.en;
        const newSport = newPaths[sportKey] || firstSegment;
        const rest = routeParts.slice(1).join('/');
        navigate(`/${newLang}/${newSport}${rest ? '/' + rest : ''}${location.search}`);
      } else {
        navigate(`/${newLang}/${routeParts.join('/')}${location.search}`);
      }
    }
  }, [currentLang, urlLang, location, navigate, i18n]);

  const buildHreflang = useCallback((routeKey, slug) => {
    return SUPPORTED_LANGUAGES.map(l => {
      const paths = PATH_TRANSLATIONS[l.code] || PATH_TRANSLATIONS.en;
      const segment = paths[routeKey] || routeKey;
      const href = slug ? `/${l.code}/${segment}/${slug}` : `/${l.code}/${segment}`;
      return { lang: l.code, href };
    }).concat([{ lang: 'x-default', href: slug ? `/en/${PATH_TRANSLATIONS.en[routeKey] || routeKey}/${slug}` : `/en/${PATH_TRANSLATIONS.en[routeKey] || routeKey}` }]);
  }, []);

  const value = useMemo(() => ({
    currentLang,
    switchLanguage,
    getLocalizedPath,
    buildHreflang,
    languages: SUPPORTED_LANGUAGES,
  }), [currentLang, switchLanguage, getLocalizedPath, buildHreflang]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}
